import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Row,
  Col,
} from 'reactstrap';
import { inventarioService } from '../../services/inventarioService';
import { productosService } from '../../services/productosService';

function ConteoFisicoForm({ sucursalId, usuarioId, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    productoID: '',
    stockFisico: 0,
    observaciones: '',
  });

  const [productos, setProductos] = useState([]);
  const [stockActual, setStockActual] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (sucursalId) {
      loadProductos();
    }
  }, [sucursalId]);

  useEffect(() => {
    if (formData.productoID && sucursalId) {
      loadStockActual();
    } else {
      setStockActual(null);
    }
  }, [formData.productoID, sucursalId]);

  const loadProductos = async () => {
    try {
      // Obtener productos filtrados por sucursal (empresaId null para obtener todos)
      const response = await productosService.getAll(null, sucursalId, null, null);
      if (response.success && response.data) {
        const productosData = Array.isArray(response.data) ? response.data : [];
        const normalized = productosData.map(prod => ({
          ProductoID: prod.ProductoID || prod.productoID,
          Nombre: prod.Nombre || prod.nombre || '',
          SKU: prod.SKU || prod.sku || '',
        }));
        setProductos(normalized);
      }
    } catch (err) {
    }
  };

  const loadStockActual = async () => {
    try {
      const productoId = parseInt(formData.productoID);
      if (!productoId) return;

      const response = await inventarioService.getStock(productoId, sucursalId);
      if (response.success && response.data) {
        // El endpoint devuelve un StockDto con StockActual
        const stockData = response.data;
        const stock = stockData.StockActual !== undefined ? stockData.StockActual : (stockData.stockActual !== undefined ? stockData.stockActual : 0);
        setStockActual(stock);
        // Establecer el stock físico inicial igual al stock actual
        setFormData(prev => ({
          ...prev,
          stockFisico: stock,
        }));
      } else {
        // Si no hay stock, establecer en 0
        setStockActual(0);
        setFormData(prev => ({
          ...prev,
          stockFisico: 0,
        }));
      }
    } catch (err) {
      setStockActual(null);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.productoID) newErrors.productoID = 'El producto es requerido';
    if (formData.stockFisico < 0) {
      newErrors.stockFisico = 'El stock físico no puede ser negativo';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);

      const conteoData = {
        SucursalID: sucursalId,
        ProductoID: parseInt(formData.productoID),
        StockFisico: parseFloat(formData.stockFisico),
        UsuarioID: usuarioId,
        Observaciones: formData.observaciones?.trim() || null,
      };

      const response = await inventarioService.registrarConteoFisico(conteoData);

      if (response.success) {
        onSuccess();
      } else {
        setError(response.Message || response.message || 'Error al registrar el conteo físico');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const diferencia = stockActual !== null ? (parseFloat(formData.stockFisico) - stockActual) : null;

  return (
    <Modal isOpen={true} toggle={onCancel} size="lg" className="modal-black">
      <ModalHeader toggle={onCancel}>Registrar Conteo Físico</ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          {error && (
            <Alert color="danger" className="mb-3">
              {error}
            </Alert>
          )}

          <Row>
            <Col md="12">
              <FormGroup>
                <Label>
                  Producto <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  name="productoID"
                  value={formData.productoID}
                  onChange={handleChange}
                  invalid={!!errors.productoID}
                  required
                >
                  <option value="">Seleccione un producto</option>
                  {productos.map((prod) => (
                    <option key={prod.ProductoID} value={prod.ProductoID}>
                      {prod.Nombre} ({prod.SKU})
                    </option>
                  ))}
                </Input>
                {errors.productoID && (
                  <div className="text-danger small">{errors.productoID}</div>
                )}
              </FormGroup>
            </Col>
          </Row>

          {stockActual !== null && (
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>Stock Teórico (Actual)</Label>
                  <Input
                    type="text"
                    value={stockActual.toFixed(2)}
                    disabled
                    className="bg-light"
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>
                    Stock Físico <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="number"
                    name="stockFisico"
                    value={formData.stockFisico}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    invalid={!!errors.stockFisico}
                    required
                  />
                  {errors.stockFisico && (
                    <div className="text-danger small">{errors.stockFisico}</div>
                  )}
                </FormGroup>
              </Col>
            </Row>
          )}

          {diferencia !== null && (
            <Row>
              <Col md="12">
                <Alert color={diferencia === 0 ? 'success' : diferencia > 0 ? 'info' : 'warning'}>
                  <strong>Diferencia:</strong> {diferencia > 0 ? '+' : ''}{diferencia.toFixed(2)}
                  {diferencia === 0 && ' (Sin diferencia)'}
                  {diferencia > 0 && ' (Sobrante)'}
                  {diferencia < 0 && ' (Faltante)'}
                </Alert>
              </Col>
            </Row>
          )}

          <Row>
            <Col md="12">
              <FormGroup>
                <Label>Observaciones</Label>
                <Input
                  type="textarea"
                  name="observaciones"
                  value={formData.observaciones}
                  onChange={handleChange}
                  rows="3"
                />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
          <Button color="primary" type="submit" disabled={loading}>
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

export default ConteoFisicoForm;

